/**
 * Staging API Client
 *
 * API client for the inline editing staging system.
 */

import { apiGet, apiPost, apiRequest } from './client';
import type { EditableSiteContent } from '../renderer/types';
import type { StagedChange } from '../renderer/context';

// ============================================================================
// Types
// ============================================================================

export type EditorType = 'admin' | 'customer' | 'system';
export type StagingStatus = 'editing' | 'published' | 'abandoned' | 'merged';

export interface StagingChange {
  oldValue: string | string[];
  newValue: string | string[];
  changedAt: string;
  changedBy?: string;
}

export interface WebsiteStaging {
  id: string;
  website_id: string;
  user_id: string | null;
  user_type: EditorType | null;
  user_name: string | null;
  base_version_id: string | null;
  changes: Record<string, StagingChange>;
  lock_holder_id: string | null;
  lock_holder_name: string | null;
  lock_acquired_at: string | null;
  status: StagingStatus;
  commit_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface LockState {
  isLocked: boolean;
  lock_holder_id: string | null;
  lock_holder_name: string | null;
  lock_acquired_at: string | null;
  canTakeover: boolean;
}

export interface StagingStateResponse {
  staging: WebsiteStaging | null;
  content: EditableSiteContent | null;
  version?: number;
  lockState?: LockState;
  hasVersion: boolean;
  message?: string;
}

export interface StartStagingResponse {
  staging: WebsiteStaging;
  isNew: boolean;
  message: string;
}

export interface PublishResponse {
  success: boolean;
  version_number?: number;
  version_id?: string;
  changes_applied?: number;
  message?: string;
  error?: string;
  broken_links?: string[];
}

export interface VersionSummary {
  id: string;
  version_number: number;
  created_by: string | null;
  created_by_type: EditorType | null;
  commit_message: string | null;
  created_at: string;
}

export interface VersionsResponse {
  versions: VersionSummary[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get the current staging state for a website
 * Returns content, staging session, and lock state
 */
export async function getStagingState(websiteId: string): Promise<StagingStateResponse> {
  return apiGet<StagingStateResponse>(`/api/staging/${websiteId}`);
}

/**
 * Start or resume an editing session
 */
export async function startStagingSession(
  websiteId: string,
  userId: string,
  userName: string,
  userType: EditorType
): Promise<StartStagingResponse> {
  return apiPost<StartStagingResponse>(`/api/staging/${websiteId}/start`, {
    userId,
    userName,
    userType,
  });
}

/**
 * Update a single element in staging
 */
export async function updateStagingElement(
  websiteId: string,
  elementId: string,
  oldValue: string | string[],
  newValue: string | string[],
  changedBy?: string,
  userId?: string
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {};
  if (userId) {
    headers['X-User-ID'] = userId;
  }
  return apiRequest<{ success: boolean }>(`/api/staging/${websiteId}/element`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({
      elementId,
      oldValue,
      newValue,
      changedBy,
    }),
  });
}

/**
 * Revert a single element to its original value
 */
export async function revertStagingElement(
  websiteId: string,
  elementId: string
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(`/api/staging/${websiteId}/element/${elementId}`, {
    method: 'DELETE',
  });
}

/**
 * Abandon the current staging session (discard all changes)
 */
export async function abandonStagingSession(
  websiteId: string
): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/api/staging/${websiteId}/abandon`);
}

/**
 * Get current lock state
 */
export async function getLockState(websiteId: string): Promise<{ lockState: LockState }> {
  return apiGet<{ lockState: LockState }>(`/api/staging/${websiteId}/lock`);
}

/**
 * Acquire the edit lock
 */
export async function acquireLock(
  websiteId: string,
  userId: string,
  userName: string
): Promise<{ success: boolean; previousHolder?: string; message: string }> {
  return apiPost<{ success: boolean; previousHolder?: string; message: string }>(
    `/api/staging/${websiteId}/lock`,
    { userId, userName }
  );
}

/**
 * Release the edit lock
 */
export async function releaseLock(
  websiteId: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest<{ success: boolean; message: string }>(`/api/staging/${websiteId}/lock`, {
    method: 'DELETE',
  });
}

/**
 * Refresh lock timestamp (heartbeat)
 */
export async function refreshLock(websiteId: string): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>(`/api/staging/${websiteId}/lock/refresh`);
}

/**
 * Publish staged changes to production
 */
export async function publishStagedChanges(
  websiteId: string,
  commitMessage?: string
): Promise<PublishResponse> {
  return apiPost<PublishResponse>(`/api/staging/${websiteId}/publish`, { commitMessage });
}

/**
 * List all versions for a website
 */
export async function listVersions(
  websiteId: string,
  limit = 20,
  offset = 0
): Promise<VersionsResponse> {
  return apiGet<VersionsResponse>(`/api/websites/${websiteId}/versions?limit=${limit}&offset=${offset}`);
}

/**
 * Revert to a specific version
 */
export async function revertToVersion(
  websiteId: string,
  versionNumber: number,
  userId?: string,
  userType?: EditorType
): Promise<{ success: boolean; version_number: number; version_id: string; message: string }> {
  return apiPost<{ success: boolean; version_number: number; version_id: string; message: string }>(
    `/api/websites/${websiteId}/versions/${versionNumber}/revert`,
    { userId, userType }
  );
}

// ============================================================================
// Helpers for converting between API and renderer types
// ============================================================================

/**
 * Convert API staging changes to renderer StagedChange format
 */
export function toStagedChanges(apiChanges: Record<string, StagingChange>): Record<string, StagedChange> {
  const result: Record<string, StagedChange> = {};
  for (const [id, change] of Object.entries(apiChanges)) {
    result[id] = {
      oldValue: change.oldValue,
      newValue: change.newValue,
      changedAt: change.changedAt,
      changedBy: change.changedBy,
    };
  }
  return result;
}

/**
 * Convert renderer StagedChange to API update payload
 */
export function fromStagedChange(change: StagedChange): {
  oldValue: string | string[];
  newValue: string | string[];
  changedBy?: string;
} {
  return {
    oldValue: change.oldValue,
    newValue: change.newValue,
    changedBy: change.changedBy,
  };
}

// ============================================================================
// AI Text Refinement
// ============================================================================

export type RefinementType = 'shorter' | 'longer' | 'friendlier' | 'professional' | 'regenerate';

export interface RefineTextResponse {
  success: boolean;
  original: string;
  refined: string;
  refinementType: RefinementType;
}

/**
 * Refine text using AI
 */
export async function refineText(
  websiteId: string,
  text: string,
  refinementType: RefinementType,
  elementLabel?: string,
  customPrompt?: string
): Promise<RefineTextResponse> {
  return apiPost<RefineTextResponse>(`/api/staging/${websiteId}/refine-text`, {
    text,
    refinementType,
    elementLabel,
    customPrompt,
  });
}
